import React, { forwardRef } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  ChatBubbleResponsivePie,
  ChatBubbleResponsiveCartesian,
  formatChartMetricValue,
  formatChartTooltipValue,
} from "../../ChatPage/ChatSectionChartHelpers";
import {
  EMCURE_CHAT_CARTESIAN_MARGIN,
  EMCURE_CHAT_Y_AXIS_WIDTH,
  EMCURE_PIE_COLORS,
  humanizeColumnLabel,
} from "./emcureChartConstants";
import ChartSeriesLines from "./ChartSeriesLines";

/**
 * Pie / bar / line charts inside a chat bubble (ChatSection message charts).
 */
const EmcureBubbleChartPlot = forwardRef(function EmcureBubbleChartPlot(
  { chartType, validData, chart, cIdx, gradIdPrefix = String(cIdx), formatDateAxis, parseTooltipNumber },
  ref
) {
  const yCol = chart?.y_column;
  const seriesKeys = chart?.seriesKeys;

  return (
    <div ref={ref} className="chart-container">
      {chartType === "pie" && (
        <ChatBubbleResponsivePie
          pieData={validData}
          pieColors={EMCURE_PIE_COLORS}
          gradIdPrefix={gradIdPrefix}
          yColumn={yCol}
        />
      )}

      {chartType === "bar" && (
        <div className="bar-chart-scroll-container bar-chart-scroll-container-styled">
          <style>
            {`
            .bar-chart-scroll-container::-webkit-scrollbar {
              height: 10px;
            }
            .bar-chart-scroll-container::-webkit-scrollbar-track {
              background: #F3F4F6;
              border-radius: 5px;
              margin: 0 10px;
            }
            .bar-chart-scroll-container::-webkit-scrollbar-thumb {
              background: #9CA3AF;
              border-radius: 5px;
            }
            .bar-chart-scroll-container::-webkit-scrollbar-thumb:hover {
              background: #6B7280;
            }
            .bar-chart-scroll-container {
              scrollbar-width: thin;
              scrollbar-color: #9CA3AF #F3F4F6;
            }
          `}
          </style>
          <ChatBubbleResponsiveCartesian height={280} minOuterWidth={280}>
            {(w, h) => (
              <BarChart
                width={w}
                height={h}
                data={validData}
                margin={EMCURE_CHAT_CARTESIAN_MARGIN}
              >
                <defs>
                  <linearGradient id={`barGradient-${cIdx}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#B90005" stopOpacity={1} />
                    <stop offset="50%" stopColor="#DC2626" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#EF4444" stopOpacity={0.8} />
                  </linearGradient>
                  <filter id={`barShadow-${cIdx}`}>
                    <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.2" />
                  </filter>
                </defs>
                <CartesianGrid
                  strokeDasharray="5 5"
                  stroke="#E5E7EB"
                  strokeOpacity={0.6}
                  vertical={false}
                />
                <XAxis
                  dataKey="xValue"
                  angle={-45}
                  textAnchor="end"
                  interval={0}
                  height={60}
                  padding={{ left: 24, right: 16 }}
                  tick={{
                    fontSize: 11,
                    fontWeight: 600,
                    fill: "#374151",
                  }}
                  stroke="#9CA3AF"
                  strokeWidth={1.5}
                  tickFormatter={(value) => {
                    const formatted = formatDateAxis(value);
                    if (formatted !== value) return String(formatted);
                    if (typeof value === "string" && value.length > 12) {
                      return value.substring(0, 12) + "...";
                    }
                    return value;
                  }}
                />
                <YAxis
                  width={EMCURE_CHAT_Y_AXIS_WIDTH}
                  tick={{
                    fontSize: 11,
                    fontWeight: 600,
                    fill: "#374151",
                  }}
                  stroke="#9CA3AF"
                  strokeWidth={1.5}
                  tickFormatter={(value) => formatChartMetricValue(value)}
                  tickMargin={5}
                />
                <Tooltip
                  wrapperStyle={{
                    pointerEvents: "none",
                    outline: "none",
                    zIndex: 50,
                  }}
                  allowEscapeViewBox={{ x: false, y: true }}
                  contentStyle={{
                    fontSize: "13px",
                    backgroundColor: "#FFFFFF",
                    borderRadius: "10px",
                    padding: "12px 16px",
                    border: "1px solid #E5E7EB",
                    fontWeight: "600",
                    maxWidth: "300px",
                    wordWrap: "break-word",
                  }}
                  formatter={(v) => [formatChartTooltipValue(v), humanizeColumnLabel(yCol)]}
                  cursor={{ fill: "rgba(30, 64, 175, 0.08)" }}
                  labelStyle={{ color: "#111827", fontWeight: "700", marginBottom: "4px" }}
                  labelFormatter={(label) => {
                    if (label == null) return "";
                    const formatted = formatDateAxis(label);
                    return formatted !== label ? String(formatted) : String(label);
                  }}
                  isAnimationActive={false}
                  animationDuration={0}
                />
                <Bar
                  dataKey="yValue"
                  fill={`url(#barGradient-${cIdx})`}
                  radius={[8, 8, 0, 0]}
                  maxBarSize={validData.length <= 15 ? undefined : 35}
                  name="Value"
                  style={{ filter: `url(#barShadow-${cIdx})` }}
                />
              </BarChart>
            )}
          </ChatBubbleResponsiveCartesian>
        </div>
      )}

      {chartType === "line" && (
        <div className="chart-line-container">
          <div
            className="chart-line-inner-responsive"
            style={{
              width: "100%",
              minWidth:
                validData.length > 15 ? Math.max(validData.length * 45 + 100, 320) : undefined,
              height: 280,
            }}
          >
            <ChatBubbleResponsiveCartesian
              height={280}
              minOuterWidth={280}
              innerMinWidth={
                validData.length > 15 ? Math.max(validData.length * 45 + 100, 320) : undefined
              }
            >
              {(w, h) => (
                <LineChart width={w} height={h} data={validData} margin={EMCURE_CHAT_CARTESIAN_MARGIN}>
                  <defs>
                    <linearGradient id={`lineGradient-${cIdx}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1E40AF" stopOpacity={1} />
                      <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.8} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="5 5" stroke="#E5E7EB" strokeOpacity={0.6} />
                  <XAxis
                    dataKey="xValue"
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={60}
                    padding={{ left: 24, right: 16 }}
                    tick={{ fontSize: 11, fontWeight: 600, fill: "#374151" }}
                    stroke="#9CA3AF"
                    strokeWidth={1.5}
                    tickFormatter={(value) => {
                      const formatted = formatDateAxis(value);
                      if (formatted !== value) return String(formatted);
                      return value;
                    }}
                  />
                  <YAxis
                    width={EMCURE_CHAT_Y_AXIS_WIDTH}
                    tick={{ fontSize: 11, fontWeight: 600, fill: "#374151" }}
                    stroke="#9CA3AF"
                    strokeWidth={1.5}
                    tickFormatter={(value) => formatChartMetricValue(value)}
                    tickMargin={5}
                  />
                  <Tooltip
                    wrapperStyle={{
                      pointerEvents: "none",
                      outline: "none",
                      zIndex: 50,
                    }}
                    allowEscapeViewBox={{ x: false, y: true }}
                    contentStyle={{
                      fontSize: "13px",
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #E5E7EB",
                      borderRadius: "10px",
                      padding: "12px 16px",
                      fontWeight: "600",
                      boxShadow: "0 10px 15px -3px rgba(15, 23, 42, 0.1), 0 4px 6px -4px rgba(15, 23, 42, 0.1)",
                    }}
                    formatter={(v, name) => [
                      formatChartTooltipValue(v),
                      seriesKeys?.length ? name : humanizeColumnLabel(yCol),
                    ]}
                    labelFormatter={(label) => {
                      if (label == null) return "";
                      const formatted = formatDateAxis(label);
                      return formatted !== label ? String(formatted) : String(label);
                    }}
                    cursor={{ stroke: "#3B82F6", strokeWidth: 1, strokeDasharray: "5 5" }}
                    isAnimationActive={false}
                    animationDuration={0}
                  />
                  {seriesKeys?.length ? (
                    <ChartSeriesLines seriesKeys={seriesKeys} />
                  ) : (
                    <Line
                      type="monotone"
                      dataKey="yValue"
                      stroke={`url(#lineGradient-${cIdx})`}
                      strokeWidth={3}
                      dot={{ fill: "#1E40AF", r: 4 }}
                      activeDot={{ r: 6 }}
                      isAnimationActive={false}
                      animationDuration={0}
                    />
                  )}
                </LineChart>
              )}
            </ChatBubbleResponsiveCartesian>
          </div>
        </div>
      )}
    </div>
  );
});

export default EmcureBubbleChartPlot;
